// ============================================================
// DuplicateComparePopup — side-by-side jmfrelse av tva filer
// vid identifierad dubblett. Dual-mode (Feature 2 + 3):
//   - 'new-upload'     : ny File vs befintliga DuplicateMatch[] (vid upload)
//   - 'existing-group' : 2+ befintliga CloudReceipt (retroaktiv skann)
// ============================================================

import { useEffect, useMemo, useRef, useState } from 'react'
import { PreviewPane } from './PreviewPane'
import {
  deleteReceiptCloud,
  updateReceiptFields,
  type CloudReceipt,
  type DuplicateMatch,
  type DupScanGroup,
} from '../lib/cloudStore'
import { receiptListTitle } from '../lib/receipts'

const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

const REASON_LABEL: Record<DuplicateMatch['reason'], string> = {
  'exact-hash':        'Identisk fil (SHA-256)',
  'same-filename':     'Samma filnamn',
  'fuzzy-amount-date': 'Liknande belopp + datum',
}

const REASON_COLOR: Record<DuplicateMatch['reason'], string> = {
  'exact-hash':        '#ef4444',
  'same-filename':     '#f59e0b',
  'fuzzy-amount-date': '#3b82f6',
}

interface NewUploadMode {
  mode: 'new-upload'
  /** Den nya filen som ska laddas upp (object URL skapas internt) */
  incomingFile: File
  /** Metadata fr den nya filen */
  incomingMeta: { filename: string; size: number; date?: string; amount?: number; currency?: string }
  /** Existerande kvitton som matchar — visas i höger panel + tabs */
  matches: DuplicateMatch[]
  onUploadAnyway: () => void
  onSkip: () => void
  onAbort: () => void
  /** Pos i ken (fr "1/12"-text) */
  progress?: { current: number; total: number }
}

interface ExistingGroupMode {
  mode: 'existing-group'
  /** Grupp av 2+ existerande kvitton som identifierats som dubletter */
  group: DupScanGroup
  companyId: string
  onClose: () => void
  /** Anropas nr en receipt tas bort, sa parent kan uppdatera sin lista */
  onDeleted?: (id: string) => void
  /** Anropas nr brukaren markerar bgge som "behll bda" (verified) */
  onKeepBoth?: () => void
}

type Props = NewUploadMode | ExistingGroupMode

export function DuplicateComparePopup(p: Props) {
  if (p.mode === 'new-upload') return <NewUploadCompare p={p} />
  return <ExistingGroupCompare p={p} />
}

// ============================================================
// MODE: new-upload — ny fil vs N matches
// ============================================================

function NewUploadCompare({ p }: { p: NewUploadMode }) {
  const [activeMatch, setActiveMatch] = useState(0)
  const incomingUrl = useMemo(() => URL.createObjectURL(p.incomingFile), [p.incomingFile])
  useEffect(() => () => URL.revokeObjectURL(incomingUrl), [incomingUrl])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') p.onAbort() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [p.onAbort])

  const m = p.matches[activeMatch] ?? p.matches[0]
  const r = m.receipt
  const reasonLabel = REASON_LABEL[m.reason]
  const reasonColor = REASON_COLOR[m.reason]

  return (
    <Shell onClose={p.onAbort}>
      <Header
        title="Möjlig dubblett"
        subtitle={p.progress ? p.progress.current + '/' + p.progress.total : undefined}
        reasonLabel={reasonLabel}
        reasonColor={reasonColor}
        onClose={p.onAbort}
        tabs={p.matches.length > 1 ? p.matches.map((mm, i) => ({
          label: 'Match ' + (i + 1) + ' av ' + p.matches.length,
          active: i === activeMatch,
          onClick: () => setActiveMatch(i),
        })) : undefined}
      />

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left: incoming new file */}
        <Pane title="Ny fil (laddas upp)" badge="NY" badgeColor="#3b82f6">
          <PreviewPane url={incomingUrl} mime={p.incomingFile.type || 'application/pdf'} filename={p.incomingMeta.filename} />
          <MetaFooter meta={{
            filename: p.incomingMeta.filename,
            sizeBytes: p.incomingMeta.size,
            date:      p.incomingMeta.date,
            amount:    p.incomingMeta.amount,
            currency:  p.incomingMeta.currency,
          }} />
        </Pane>

        <Divider />

        {/* Right: existing match */}
        <Pane title="Existerande kvitto" badge="REDAN UPPLADDAT" badgeColor={reasonColor}>
          <PreviewPane url={r.downloadUrl} mime={r.mime} filename={r.filename} sourcePath={r.sourcePath} />
          <MetaFooter meta={{
            filename:  r.filename,
            sizeBytes: r.size,
            date:      r.date,
            amount:    r.amount,
            currency:  r.currency,
            title:     r.title,
          }} />
        </Pane>
      </div>

      {/* Footer actions */}
      <div style={{
        display: 'flex', gap: '0.5rem', padding: '0.6rem 1rem',
        borderTop: '1px solid var(--border)', flexShrink: 0,
        background: 'rgba(0,0,0,0.2)',
      }}>
        <button
          onClick={p.onAbort}
          style={btnStyle('danger')}
        >
          Avbryt hela importen
        </button>
        <button
          onClick={p.onSkip}
          style={btnStyle('neutral')}
        >
          Hoppa över denna
        </button>
        <span style={{ flex: 1 }} />
        <button
          onClick={p.onUploadAnyway}
          style={btnStyle('warning')}
        >
          Ladda upp ändå (dubblett)
        </button>
      </div>
    </Shell>
  )
}

// ============================================================
// MODE: existing-group — 2+ existerande receipts
// ============================================================

function ExistingGroupCompare({ p }: { p: ExistingGroupMode }) {
  const [deleting, setDeleting] = useState<string | null>(null)
  const [keeping, setKeeping] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  // Index fr aktiv "left" och "right" — anvnds nr gruppen har 3+ filer
  const [leftIdx, setLeftIdx] = useState(0)
  const [rightIdx, setRightIdx] = useState(1)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') p.onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [p.onClose])

  const receipts = p.group.receipts
  const reasonLabel = REASON_LABEL[p.group.reason]
  const reasonColor = REASON_COLOR[p.group.reason]

  // Hll left/right inom valid range om receipts ndras (ngon togs bort)
  useEffect(() => {
    if (leftIdx >= receipts.length) setLeftIdx(0)
    if (rightIdx >= receipts.length) setRightIdx(Math.min(1, receipts.length - 1))
    if (leftIdx === rightIdx && receipts.length > 1) setRightIdx((leftIdx + 1) % receipts.length)
  }, [receipts.length, leftIdx, rightIdx])

  if (receipts.length < 2) {
    return null
  }

  const left  = receipts[Math.min(leftIdx,  receipts.length - 1)]
  const right = receipts[Math.min(rightIdx, receipts.length - 1)]

  async function doDelete(r: CloudReceipt) {
    if (!confirm('Ta bort "' + r.filename + '" permanent? Detta kan inte ngras.')) return
    setDeleting(r.id)
    setMsg(null)
    try {
      await deleteReceiptCloud(p.companyId, r)
      setMsg('Borttaget: ' + r.filename)
      p.onDeleted?.(r.id)
    } catch (e) {
      setMsg('Fel: ' + (e as Error).message)
    } finally {
      setDeleting(null)
    }
  }

  async function doKeepBoth() {
    setKeeping(true)
    setMsg(null)
    try {
      // Markera bda som "verifierade" (manuellt kontrollerade) — innebr att
      // scanAllDuplicates skippar dem i fuzzy-passet i framtiden
      for (const r of receipts) {
        await updateReceiptFields(p.companyId, r.id, {})
      }
      setMsg('Markerade ' + receipts.length + ' kvitton som verifierade.')
      p.onKeepBoth?.()
      setTimeout(() => p.onClose(), 800)
    } catch (e) {
      setMsg('Fel: ' + (e as Error).message)
    } finally {
      setKeeping(false)
    }
  }

  return (
    <Shell onClose={p.onClose}>
      <Header
        title="Dubblett-jmfrelse"
        subtitle={receipts.length + ' kvitton i gruppen'}
        reasonLabel={reasonLabel}
        reasonColor={reasonColor}
        onClose={p.onClose}
      />

      {/* Picker fr left/right nr 3+ receipts */}
      {receipts.length > 2 && (
        <div style={{
          display: 'flex', gap: '0.5rem', padding: '0.4rem 1rem',
          borderBottom: '1px solid var(--border)', flexShrink: 0,
          background: 'rgba(0,0,0,0.15)', fontSize: '0.8rem',
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>Vänster:</span>
            <select value={leftIdx} onChange={(e) => setLeftIdx(Number(e.target.value))} style={selectStyle}>
              {receipts.map((r, i) => (
                <option key={r.id} value={i}>{(i + 1) + '. ' + receiptListTitle(r)}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>Höger:</span>
            <select value={rightIdx} onChange={(e) => setRightIdx(Number(e.target.value))} style={selectStyle}>
              {receipts.map((r, i) => (
                <option key={r.id} value={i}>{(i + 1) + '. ' + receiptListTitle(r)}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      {msg && (
        <div style={{
          padding: '0.4rem 1rem', borderBottom: '1px solid var(--border)',
          fontSize: '0.85rem', flexShrink: 0,
          background: msg.startsWith('Fel') ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)',
          color: msg.startsWith('Fel') ? '#ef4444' : '#10b981',
        }}>
          {msg}
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Pane title={left.title || left.filename} badge="VÄNSTER" badgeColor="#64748b">
          <PreviewPane url={left.downloadUrl} mime={left.mime} filename={left.filename} sourcePath={left.sourcePath} />
          <MetaFooter meta={{
            filename: left.filename, sizeBytes: left.size,
            date: left.date, amount: left.amount, currency: left.currency,
            title: left.title,
          }} />
        </Pane>
        <Divider />
        <Pane title={right.title || right.filename} badge="HÖGER" badgeColor="#64748b">
          <PreviewPane url={right.downloadUrl} mime={right.mime} filename={right.filename} sourcePath={right.sourcePath} />
          <MetaFooter meta={{
            filename: right.filename, sizeBytes: right.size,
            date: right.date, amount: right.amount, currency: right.currency,
            title: right.title,
          }} />
        </Pane>
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex', gap: '0.5rem', padding: '0.6rem 1rem',
        borderTop: '1px solid var(--border)', flexShrink: 0,
        background: 'rgba(0,0,0,0.2)',
      }}>
        <button
          onClick={() => doDelete(left)}
          disabled={deleting === left.id || deleting === right.id}
          style={btnStyle('danger')}
        >
          {deleting === left.id ? 'Tar bort...' : ' Ta bort vänster'}
        </button>
        <button
          onClick={() => doDelete(right)}
          disabled={deleting === left.id || deleting === right.id}
          style={btnStyle('danger')}
        >
          {deleting === right.id ? 'Tar bort...' : 'Ta bort höger '}
        </button>
        <span style={{ flex: 1 }} />
        <button
          onClick={doKeepBoth}
          disabled={keeping || !!deleting}
          style={btnStyle('neutral')}
        >
          {keeping ? 'Markerar...' : ' Behll bda (markera som verifierade)'}
        </button>
        <button
          onClick={p.onClose}
          style={btnStyle('neutral')}
        >
          Stng
        </button>
      </div>
    </Shell>
  )
}

// ============================================================
// Shared UI primitives
// ============================================================

function Shell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column', zIndex: 10000 }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, margin: '0.5rem', flex: 1,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}

function Header({ title, subtitle, reasonLabel, reasonColor, onClose, tabs }: {
  title: string
  subtitle?: string
  reasonLabel: string
  reasonColor: string
  onClose: () => void
  tabs?: { label: string; active: boolean; onClick: () => void }[]
}) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.6rem 1rem', borderBottom: '1px solid var(--border)', flexShrink: 0,
      flexWrap: 'wrap', gap: '0.5rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '1rem', fontWeight: 700 }}>{title}</div>
        <span style={{
          background: reasonColor + '22', color: reasonColor,
          padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600,
        }}>
          {reasonLabel}
        </span>
        {subtitle && (
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{subtitle}</span>
        )}
        {tabs && (
          <div style={{ display: 'flex', gap: '0.25rem', marginLeft: '0.5rem' }}>
            {tabs.map((t, i) => (
              <button
                key={i}
                onClick={t.onClick}
                style={{
                  background: t.active ? 'var(--primary)' : 'rgba(255,255,255,0.05)',
                  color:      t.active ? '#fff' : 'var(--text-muted)',
                  border: '1px solid var(--border)', borderRadius: 4,
                  padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <button onClick={onClose} style={{
        background: 'none', border: 'none', color: 'var(--text-muted)',
        fontSize: '1.3rem', cursor: 'pointer', padding: '0.25rem',
      }}>{''}</button>
    </div>
  )
}

function Pane({ title, badge, badgeColor, children }: {
  title: string
  badge: string
  badgeColor: string
  children: React.ReactNode
}) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
      <div style={{
        padding: '0.4rem 0.6rem', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0,
        background: 'rgba(0,0,0,0.15)',
      }}>
        <span style={{
          background: badgeColor + '22', color: badgeColor,
          padding: '1px 6px', borderRadius: 4, fontSize: '0.65rem', fontWeight: 700,
          letterSpacing: '0.04em',
        }}>{badge}</span>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={title}>
          {title}
        </span>
      </div>
      {children}
    </div>
  )
}

function Divider() {
  return <div style={{ width: 4, background: 'var(--border)', flexShrink: 0 }} />
}

function MetaFooter({ meta }: {
  meta: { filename: string; sizeBytes?: number; date?: string; amount?: number; currency?: string; title?: string }
}) {
  return (
    <div style={{
      padding: '0.4rem 0.6rem', borderTop: '1px solid var(--border)',
      fontSize: '0.72rem', color: 'var(--text-muted)', flexShrink: 0,
      display: 'flex', flexWrap: 'wrap', gap: '0.5rem',
      background: 'rgba(0,0,0,0.1)',
    }}>
      {meta.title && meta.title !== meta.filename && (
        <span title={meta.title} style={{ color: 'var(--text)' }}>{meta.title}</span>
      )}
      <span title={meta.filename} style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {meta.filename}
      </span>
      {meta.sizeBytes != null && (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{(meta.sizeBytes / 1024).toFixed(0) + ' KB'}</span>
      )}
      {meta.date && <span style={{ fontVariantNumeric: 'tabular-nums' }}>{meta.date}</span>}
      {meta.amount != null && (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(meta.amount) + ' ' + (meta.currency ?? '')}</span>
      )}
    </div>
  )
}

const selectStyle: React.CSSProperties = {
  background: 'var(--bg-card)', color: 'var(--text)',
  border: '1px solid var(--border)', borderRadius: 4,
  padding: '2px 6px', fontSize: '0.8rem', maxWidth: 280,
}

function btnStyle(kind: 'warning' | 'danger' | 'neutral'): React.CSSProperties {
  const base: React.CSSProperties = {
    border: 'none', borderRadius: 6,
    padding: '0.45rem 0.9rem', fontSize: '0.85rem',
    fontWeight: 600, cursor: 'pointer',
  }
  if (kind === 'warning') return { ...base, background: '#f59e0b', color: '#000' }
  if (kind === 'danger')  return { ...base, background: 'transparent', color: 'var(--danger)', border: '1px solid var(--danger)' }
  return { ...base, background: 'rgba(255,255,255,0.08)', color: 'var(--text)', border: '1px solid var(--border)' }
}
